import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Target, Calendar, TrendingUp, Flame, Trophy } from 'lucide-react';
import { theme, cardStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  TodaysGoalsCard — morning motivation panel. Shows today's appointment
//  count, expected/realised revenue and progress toward the daily target
//  (monthly production target ÷ 25 working days). Styled to look like a
//  game scoreboard so the doctor knows what to hit today.
// ═══════════════════════════════════════════════════════════════════

const WORKING_DAYS_PER_MONTH = 25;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isToday = (dateVal) => {
  if (!dateVal) return false;
  const d = new Date(dateVal);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};

const fmtINR = (n) => `\u20B9${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const TodaysGoalsCard = ({ API_BASE, currentUser, monthlyTarget = 500000, todaysRevenue = 0, onNavigate }) => {
  const [appts, setAppts] = useState([]);
  const [expectedRev, setExpectedRev] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!API_BASE) return;
    const iso = todayISO();
    Promise.all([
      axios.get(`${API_BASE}/appointments`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/treatments`).catch(() => ({ data: [] })),
    ]).then(([apRes, txRes]) => {
      const todaysAppts = (apRes.data || []).filter(a => a.date === iso && a.status !== 'Cancelled');
      setAppts(todaysAppts);

      const plannedToday = (txRes.data || []).filter(t => {
        if (t.status === 'planned') return isToday(t.plannedDate);
        if (t.status === 'done')    return isToday(t.doneDate);
        return false;
      });
      setExpectedRev(plannedToday.reduce((s, t) => s + (Number(t.totalAmount) || 0), 0));
    }).finally(() => setLoading(false));
  }, [API_BASE]);

  if (loading) return null;

  const dailyTarget = Math.round((monthlyTarget || 0) / WORKING_DAYS_PER_MONTH);
  const realisedRev = Number(todaysRevenue) || 0;
  const projectedRev = Math.max(realisedRev, expectedRev);
  const pct = dailyTarget > 0 ? Math.min(Math.round((projectedRev / dailyTarget) * 100), 999) : 0;
  const onTrack = pct >= 80;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const motivate = (() => {
    if (appts.length === 0) return 'No appointments booked yet — good day for follow-up calls and recare.';
    if (pct >= 100) return `You've already hit today's target! Every rupee after this is bonus.`;
    if (pct >= 80)  return `Almost there — just ${fmtINR(dailyTarget - projectedRev)} left to hit today's goal.`;
    if (pct >= 50)  return `Halfway to today's goal. Close one more case acceptance and you're there.`;
    return `${appts.length} appointment${appts.length > 1 ? 's' : ''} today — focus on case acceptance to reach ${fmtINR(dailyTarget)}.`;
  })();

  const accent = onTrack ? theme.success : pct >= 50 ? theme.warning : theme.primary;

  return (
    <div style={{
      ...cardStyle, padding: 0, marginBottom: 14, overflow: 'hidden',
      border: `1px solid ${accent}33`,
      background: `linear-gradient(135deg, ${accent}14, var(--color-bg-card) 60%)`,
    }}>
      <div style={{
        padding: '10px 16px', background: accent, color: 'white',
        display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700,
        letterSpacing: 0.3,
      }}>
        <Trophy size={14} />
        TODAY'S GOAL · {greeting}{currentUser?.fullName ? `, ${currentUser.fullName.split(' ')[0]}` : ''}
        <span style={{ marginLeft: 'auto', opacity: 0.9, fontWeight: 600, fontSize: 11.5 }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
        </span>
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 14 }}>
          <Tile
            icon={<Calendar size={18} color={theme.info} />}
            label="Today's Appointments"
            value={appts.length}
            sub={appts.length > 0 ? `${appts.filter(a => a.status === 'PT Out').length} seen · ${appts.filter(a => a.status === 'Scheduled').length} upcoming` : 'book or follow up'}
            color={theme.info}
            onClick={() => onNavigate && onNavigate('calendar')}
          />
          <Tile
            icon={<TrendingUp size={18} color={theme.purple} />}
            label="Expected Revenue"
            value={fmtINR(projectedRev)}
            sub={realisedRev > 0 ? `${fmtINR(realisedRev)} already collected` : 'planned for today'}
            color={theme.purple}
          />
          <Tile
            icon={<Target size={18} color={accent} />}
            label="Daily Target"
            value={fmtINR(dailyTarget)}
            sub={`${pct}% achieved`}
            color={accent}
            highlight
          />
        </div>

        {/* Progress bar */}
        <div style={{
          height: 10, borderRadius: 999, background: 'var(--color-bg-inset)', overflow: 'hidden',
          border: `1px solid ${accent}22`,
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(pct, 100)}%`,
            background: `linear-gradient(90deg, ${accent}, ${accent}dd)`,
            transition: 'width 0.6s ease',
          }}/>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <Flame size={14} color={accent} />
          <span style={{ fontSize: 12.5, color: theme.navy, fontWeight: 600 }}>
            {motivate}
          </span>
        </div>
      </div>
    </div>
  );
};

const Tile = ({ icon, label, value, sub, color, highlight, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: 12, borderRadius: 10,
      // Theme-aware backgrounds — solid bg-card so text contrast stays
      // strong in every solar phase (dark mode card was light hex before).
      background: highlight ? `${color}1A` : 'var(--color-bg-card)',
      border: `1px solid ${color}33`,
      cursor: onClick ? 'pointer' : 'default',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon}
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 11.5, color: 'var(--color-text-secondary)' }}>{sub}</div>
  </div>
);

export default TodaysGoalsCard;
